export type ShareId = 'Facebook' | 'LinkedIn' | 'Twitter'
export interface IShareProps {
  url: string
  title?: string
  hashtags?: string
}

/**
 * This is a base URL builder class to inherit.
 * There are three implementations Facebook, LinkedIn and Twitter.
 * Each implementation defines its own host and query properties which
 * are being used in the base class to constuct the result.
 * URL construction logic is common and lives inside the base class.
 */
class URLBuilder {
  /**
   * Inherited classes should define their own hosts
   * to be used for the result URL construction.
   */
  protected host: string
  /**
   * This record holds query string properties, for example,
   * { u: 'foo', t: 'bar' } would be transformed into a string `u=foo&t=bar`
   * Inherited classes should only define the query record.
   */
  protected query: Record<string, string>
  protected props: IShareProps

  constructor(props: IShareProps) {
    this.host = ''
    this.query = {}
    this.props = props
    this.buildQuery()
  }

  public getUrl() {
    return `${this.host}?${this.getQueryString()}`
  }

  protected buildQuery() {
    return
  }

  private getQueryString() {
    return Object.keys(this.query)
      .map(
        key =>
          `${encodeURIComponent(key)}=${encodeURIComponent(this.query[key])}`
      )
      .join('&')
  }
}

/**
 * Facebook URL Builder.
 * ```
 * new FacebookURLBuilder({ url: 'https://toptal.com' }).getUrl()
 * ```
 */
export class FacebookURLBuilder extends URLBuilder {
  constructor(props: IShareProps) {
    super(props)
    this.host = 'https://www.facebook.com/sharer.php'
  }
  public buildQuery() {
    this.query = {
      u: this.props.url,
      ...(this.props.title && { t: this.props.title })
    }
  }
}

/**
 * Linkedin URL Builder.
 * ```
 * new LinkedInURLBuilder({ url: 'https://toptal.com' }).getUrl()
 * ```
 */
export class LinkedInURLBuilder extends URLBuilder {
  constructor(props: IShareProps) {
    super(props)
    this.host = 'https://www.linkedin.com/shareArticle'
  }
  public buildQuery() {
    this.query = {
      mini: 'true',
      url: this.props.url,
      ...(this.props.title && { title: this.props.title })
    }
  }
}

/**
 * Twitter URL Builder.
 * ```
 * new TwitterURLBuilder({ url: 'https://toptal.com' }).getUrl()
 * ```
 */
export class TwitterURLBuilder extends URLBuilder {
  constructor(props: IShareProps) {
    super(props)
    this.host = 'https://twitter.com/intent/tweet'
  }
  public buildQuery() {
    this.query = {
      url: this.props.url,
      ...(this.props.title && { text: this.props.title }),
      ...(this.props.hashtags && { hashtags: this.props.hashtags })
    }
  }
}
